import { Injectable } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { NavController } from '@ionic/angular';
import { AuthService } from './auth.service';
import { AuthStateService } from './auth-state.service';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { LoggerService } from '../../services/logger.service';
import { environment } from '../../environments/environment';

/**
 * Marks a request that has already been retried after a token refresh. If
 * the retry still returns 401, it cannot be a stale-token problem — most
 * likely the user lacks the required permission for the endpoint. Without
 * this marker the interceptor would try to refresh again, fail, and log
 * the user out.
 */
const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

// Request URLs that must NOT have auth headers attached.
//
// Two flavours mixed together:
// 1. API endpoints that support unauthenticated access (e.g. token-based
//    rehearsal sharing) — a stale/missing Bearer token would otherwise
//    trigger a refresh + redirect to /auth, bouncing external users out
//    of the flow. All rehearsal endpoints are token-guarded on the
//    backend (see SecurityConfig#PUBLIC_API_PATTERNS), so they're safe
//    to treat as public on the frontend regardless of whether the caller
//    is authenticated.
// 2. Static-asset fetches that just happen to go through HttpClient —
//    notably the ngx-translate loader's `./assets/i18n/<lang>.json`
//    requests. These don't need auth, and (post-P0.6) the dev-only
//    `assertTokenInDev()` would otherwise throw on every translation
//    load that races with login.
//
// Matched as substrings against the request URL, so include enough
// prefix (`/api/`, `assets/`) to avoid false positives.
const PUBLIC_ENDPOINTS = [
    '/authenticate',                  // login (POST /api/v1/authenticate)
    '/auth/request-password-reset',   // sends the reset email
    '/auth/reset-password',           // completes the reset using the email token
    '/authentication/refresh-token',  // token refresh handshake
    '/api/v1/rehearsals',             // token-guarded rehearsal sharing
    '/api/v2/rehearsals',
    'assets/'                         // ngx-translate JSON, other static fetches
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject = new BehaviorSubject<string | null>(null);

    constructor(
        private readonly authService: AuthService,
        private readonly authStateService: AuthStateService,
        private readonly navController: NavController,
        private readonly logger: LoggerService
    ) {}

    // ============ Public API ============

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isPublicEndpoint(req.url)) {
            return next.handle(req);
        }

        return this.handleAuthenticatedRequest(req, next);
    }

    // ============ Request Handling ============

    private handleAuthenticatedRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.executeWithAuth(req, next).pipe(
            catchError((error: HttpErrorResponse) => this.handleError(error, req, next))
        );
    }

    private executeWithAuth(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return from(this.addAuthHeaders(req)).pipe(
            switchMap(authReq => next.handle(authReq))
        );
    }

    // ============ Error Handling ============

    private handleError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isUnauthorizedError(error)) {
            // Already retried after a refresh: this isn't a stale-token issue,
            // so don't loop into another refresh / logout. Propagate the error
            // to the caller (e.g. the backend may have returned 401 for a
            // permission denial — it should be 403, but until that's fixed
            // we still want the component to see it).
            if (req.context.get(RETRIED_AFTER_REFRESH)) {
                this.logger.warn('[AuthInterceptor] 401 after token refresh — propagating to caller');
                return throwError(() => error);
            }
            return this.handle401Error(req, next);
        }
        if (this.isForbiddenError(error)) {
            return this.handle403Error(error);
        }
        return throwError(() => error);
    }

    private isUnauthorizedError(error: HttpErrorResponse): boolean {
        return error.status === HttpStatusCode.Unauthorized;
    }

    private isForbiddenError(error: HttpErrorResponse): boolean {
        return error.status === HttpStatusCode.Forbidden;
    }

    /**
     * Handle 403 Forbidden errors.
     * This can occur when user tries to access a show they're not linked to
     * (e.g., stale showId in storage). Log the error and let the caller handle it.
     * We don't logout because the user's session is still valid.
     */
    private handle403Error(error: HttpErrorResponse): Observable<never> {
        this.logger.warn('[AuthInterceptor] 403 Forbidden - user may not have access to this resource');
        // Don't logout - just propagate the error to be handled by the component
        return throwError(() => error);
    }

    // ============ Token Refresh ============

    private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isRefreshing) {
            return this.waitForTokenRefresh(req, next);
        }
        return this.performTokenRefresh(req, next);
    }

    private performTokenRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.startRefresh();

        // catchError BEFORE switchMap so it only catches refresh-token failures.
        // Errors from the retried request inside switchMap must propagate up,
        // not be treated as a refresh failure (which would log the user out).
        return from(this.authService.refreshToken()).pipe(
            catchError((error: HttpErrorResponse | Error) => {
                this.logger.error('[AuthInterceptor] Token refresh failed');
                return this.onRefreshFailure(error);
            }),
            switchMap((newToken: string | null) => {
                if (!newToken) {
                    return this.onRefreshFailure(new Error('No token received from refresh'));
                }
                return this.onRefreshSuccess(newToken, req, next);
            })
        );
    }

    private waitForTokenRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.refreshTokenSubject.pipe(
            filter((token): token is string => token !== null),
            take(1),
            switchMap(() => {
                const retriedReq = req.clone({
                    context: req.context.set(RETRIED_AFTER_REFRESH, true)
                });
                return this.executeWithAuth(retriedReq, next);
            })
        );
    }

    // ============ Refresh State Management ============

    private startRefresh(): void {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);
    }

    private completeRefresh(token: string | null): void {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(token);
    }

    private onRefreshSuccess(newToken: string, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.completeRefresh(newToken);
        const retriedReq = req.clone({
            context: req.context.set(RETRIED_AFTER_REFRESH, true)
        });
        return this.executeWithAuth(retriedReq, next);
    }

    private onRefreshFailure(error: unknown): Observable<never> {
        this.logger.warn('[AuthInterceptor] Refresh failed, logging out user');
        this.completeRefresh(null);
        this.authService.logout();
        this.navController.navigateRoot(['/auth'], {
            queryParams: { sessionExpired: true }
        });
        return throwError(() => error);
    }

    // ============ Header Management ============

    private async addAuthHeaders(req: HttpRequest<any>): Promise<HttpRequest<any>> {
        const [token, showId] = await this.getAuthCredentials();
        this.assertTokenInDev(req, token);
        return this.cloneWithHeaders(req, token, showId);
    }

    /**
     * Dev-only sanity check: fires when a request that isn't on
     * `PUBLIC_ENDPOINTS` reaches the interceptor without a token. With
     * the duplicate per-service `buildHeaders()` helpers gone (P0.6),
     * the interceptor is the single source of truth for auth headers,
     * so a missing token here is a real bug — either the caller fired
     * a request before login, or the endpoint should be public. We
     * throw rather than silently `Authorization: Bearer null` so the
     * misuse is loud during development. Production keeps the previous
     * fail-quiet behaviour to avoid hard-crashing on transient cold-
     * start states.
     */
    private assertTokenInDev(req: HttpRequest<any>, token: string | null): void {
        if (environment.production || token) {
            return;
        }
        throw new Error(
            `[AuthInterceptor] Authenticated request to ${req.url} reached the ` +
            `interceptor without a token. Either the caller fired before login, ` +
            `or this endpoint should be added to PUBLIC_ENDPOINTS in auth-interceptor.ts.`
        );
    }

    private async getAuthCredentials(): Promise<[string | null, number | null]> {
        const token = await this.authService.getActiveUserToken();
        const showId = this.authStateService.activeShowId;
        return [token, showId];
    }

    private cloneWithHeaders(req: HttpRequest<any>, token: string | null, showId: number | null): HttpRequest<any> {
        return req.clone({
            setHeaders: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                Authorization: `Bearer ${token}`,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                Show: `${showId}`
            }
        });
    }

    // ============ Endpoint Classification ============

    private isPublicEndpoint(url: string): boolean {
        return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
    }
}
